// 09-10 (Pau aprobó la maqueta «mantener el dedo», iPad): mantener el dedo ~0,5 s sobre un agente abre su menú, como
// el clic derecho con ratón (regla mobile-first: pulsación larga = clic derecho). Moverse más de 8 px lo cancela, así
// que desplazar la oficina con el dedo sigue igual; y el clic que manda el navegador al levantar el dedo NO abre el
// chat (lo consume `consumir`). Con ratón no hace nada: el ratón tiene su clic derecho.
// No es hooks/useLongPress: ese escucha eventos táctiles de UN elemento y no se come el clic de después.
import { useEffect, useRef } from 'react';

export const PULSACION_LARGA_MS = 500;
export const TOLERANCIA_PULSACION_PX = 8;

interface Pulsacion {
  pointerType: string;
  pointerId: number;
  clientX: number;
  clientY: number;
  button: number;
}

export function usePulsacionLarga(alDisparar: (id: string) => void) {
  const parar = useRef<() => void>(() => undefined);
  const disparada = useRef(false);
  const alDispararRef = useRef(alDisparar);
  alDispararRef.current = alDisparar;
  useEffect(() => () => parar.current(), []);

  /** En cada pointerdown (con `habilitada` = false solo olvida la anterior: p. ej. en «Ordenar», el dedo arrastra). */
  const empezar = (e: Pulsacion, id: string, habilitada = true) => {
    // Un pointerdown nuevo es otra interacción: lo de la anterior ya no cuenta.
    disparada.current = false;
    parar.current();
    if (!habilitada || e.pointerType === 'mouse' || e.button !== 0) return;
    const { pointerId, clientX: x0, clientY: y0 } = e;
    let reloj: ReturnType<typeof setTimeout> | undefined;
    const mover = (ev: PointerEvent) => {
      if (ev.pointerId === pointerId && Math.hypot(ev.clientX - x0, ev.clientY - y0) > TOLERANCIA_PULSACION_PX) fin();
    };
    const soltar = (ev: PointerEvent) => {
      if (ev.pointerId === pointerId) fin();
    };
    const fin = () => {
      clearTimeout(reloj);
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', soltar);
      parar.current = () => undefined;
    };
    parar.current = fin;
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    // El navegador cancela el puntero cuando el dedo empieza a desplazar la oficina.
    window.addEventListener('pointercancel', soltar);
    reloj = setTimeout(() => {
      fin();
      disparada.current = true;
      alDispararRef.current(id);
      // Si iOS no manda el clic al levantar el dedo, que no se coma el siguiente (p. ej. un Intro con teclado).
      const olvidar = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        window.removeEventListener('pointerup', olvidar);
        setTimeout(() => (disparada.current = false), 400);
      };
      window.addEventListener('pointerup', olvidar);
    }, PULSACION_LARGA_MS);
  };

  /** ¿Este clic es el de levantar el dedo tras abrir el menú? Si lo es, se lo come (una vez). */
  const consumir = (): boolean => {
    const d = disparada.current;
    disparada.current = false;
    return d;
  };

  return { empezar, consumir };
}
